import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { SlidersHorizontal } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";

const ROASTS = ["all", "Medie", "Intensă"];
const TYPES = [["all", "Toate"], ["boabe", "Boabe"], ["măcinată", "Măcinată"], ["capsule", "Capsule"]];
const AVAIL = [["all", "Toate"], ["in", "În stoc"], ["out", "Epuizate"]];
const SORTS = [
  { v: "popular", l: "Cele mai populare" },
  { v: "newest", l: "Cele mai noi" },
  { v: "bestseller", l: "Cele mai vândute" },
  { v: "rating", l: "Cele mai apreciate" },
  { v: "discount", l: "Reduceri" },
  { v: "price_asc", l: "Preț crescător" },
  { v: "price_desc", l: "Preț descrescător" },
  { v: "name", l: "Nume A–Z" },
];

export default function Catalog() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [brandList, setBrandList] = useState(["all"]);
  const [brand, setBrand] = useState("all");
  const [type, setType] = useState("all");
  const [roast, setRoast] = useState("all");
  const [availability, setAvailability] = useState("all");
  const [onSale, setOnSale] = useState(false);
  const [maxIntensity, setMaxIntensity] = useState(13);
  const [sort, setSort] = useState("popular");
  const q = params.get("q") || "";

  useEffect(() => {
    api.get("/brands/catalog").then(({ data }) => setBrandList(["all", ...(data || []).map((b) => b.name)]));
  }, []);

  useEffect(() => {
    setLoading(true);
    const search = new URLSearchParams({ brand, type, roast, availability, sort, intensity_max: String(maxIntensity) });
    if (onSale) search.set("on_sale", "true");
    if (q) search.set("q", q);
    api.get(`/products?${search.toString()}`).then(({ data }) => {
      setProducts(data);
      setLoading(false);
    });
  }, [brand, type, roast, availability, onSale, maxIntensity, sort, q]);

  const clearSearch = () => { params.delete("q"); setParams(params); };

  return (
    <div className="pt-20">
      <Seo title="Produse — cafea boabe, măcinată și capsule" description="Catalog Coffee Cup Supply: Lavazza, Kimbo, Jacobs. Filtrează după brand, tip, intensitate, preț și disponibilitate." />
      <section className="bg-ink text-cream">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28">
          <p className="overline text-sand">Catalog</p>
          <h1 className="font-display text-5xl md:text-8xl tracking-tight mt-3">
            {q ? `Rezultate: „${q}”` : "Produsele noastre"}
          </h1>
          {q && (
            <button onClick={clearSearch} data-testid="clear-search" className="mt-4 overline text-cream/60 hover:text-sand">
              Șterge căutarea ✕
            </button>
          )}
        </div>
      </section>

      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-14 grid lg:grid-cols-[260px_1fr] gap-12">
        <aside className="lg:sticky lg:top-28 h-fit space-y-9" data-testid="filters-panel">
          <div className="flex items-center gap-2 text-roast"><SlidersHorizontal size={18} /><span className="overline">Filtre</span></div>

          <FilterGroup label="Brand">
            <div className="flex flex-col gap-1">
              {brandList.map((b) => (
                <button key={b} onClick={() => setBrand(b)} data-testid={`filter-brand-${b}`}
                  className={`text-left py-2 text-base transition-colors ${brand === b ? "text-roast font-semibold" : "text-ink/80 hover:text-ink"}`}>
                  {b === "all" ? "Toate brandurile" : b}
                </button>
              ))}
            </div>
          </FilterGroup>

          <FilterGroup label="Tip cafea">
            <div className="flex flex-wrap gap-2">
              {TYPES.map(([v, l]) => (
                <button key={v} onClick={() => setType(v)} data-testid={`filter-type-${v}`}
                  className={`px-4 py-2.5 text-sm border transition-colors ${type === v ? "bg-ink text-cream border-ink" : "border-line hover:border-ink"}`}>{l}</button>
              ))}
            </div>
          </FilterGroup>

          <FilterGroup label="Tip prăjire">
            <div className="flex flex-wrap gap-2">
              {ROASTS.map((r) => (
                <button key={r} onClick={() => setRoast(r)} data-testid={`filter-roast-${r}`}
                  className={`px-5 py-2.5 text-sm border transition-colors ${roast === r ? "bg-ink text-cream border-ink" : "border-line hover:border-ink"}`}>
                  {r === "all" ? "Toate" : r}</button>
              ))}
            </div>
          </FilterGroup>

          <FilterGroup label="Disponibilitate">
            <div className="flex flex-wrap gap-2">
              {AVAIL.map(([v, l]) => (
                <button key={v} onClick={() => setAvailability(v)} data-testid={`filter-avail-${v}`}
                  className={`px-4 py-2.5 text-sm border transition-colors ${availability === v ? "bg-ink text-cream border-ink" : "border-line hover:border-ink"}`}>{l}</button>
              ))}
            </div>
          </FilterGroup>

          <FilterGroup label={`Intensitate maximă: ${maxIntensity}`}>
            <input type="range" min="1" max="13" value={maxIntensity} onChange={(e) => setMaxIntensity(Number(e.target.value))}
              data-testid="filter-intensity" className="w-full accent-roast" />
          </FilterGroup>

          <label className="flex items-center gap-3 text-sm cursor-pointer">
            <input type="checkbox" checked={onSale} onChange={(e) => setOnSale(e.target.checked)} data-testid="filter-onsale" className="accent-roast h-4 w-4" />
            Doar produse la reducere
          </label>
        </aside>

        <div>
          <div className="flex items-center justify-between border-b border-line pb-4 mb-10">
            <span className="text-sm text-muted-foreground" data-testid="results-count">
              {loading ? "Se încarcă..." : `${products.length} produse`}
            </span>
            <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="sort-select"
              className="bg-transparent text-sm outline-none cursor-pointer border-b border-transparent hover:border-ink">
              {SORTS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
          </div>

          {!loading && products.length === 0 ? (
            <p className="py-20 text-center text-muted-foreground" data-testid="no-results">
              Niciun produs nu corespunde filtrelor selectate.
            </p>
          ) : (
            <motion.div layout className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12">
              {products.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}

const FilterGroup = ({ label, children }) => (
  <Reveal>
    <p className="overline text-ink/50 mb-4">{label}</p>
    {children}
  </Reveal>
);
